import { createContext, useEffect, useReducer, useMemo, useRef, useState } from 'react';

// reducer - state management
import { LOGIN, LOGOUT, INITIALIZED, RELOAD_PERMISSIONS, UPDATE_PROFILE } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { initializePreferences, clearPreferences } from 'utils/preferencesSync';

// Constants
const AUTH_STORAGE_KEY = 'mantis-react-ts-auth';

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| SESSION AUTH CONTEXT & PROVIDER ||============================== //

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const checkAuthCalled = useRef(false);
  const preferencesInitRef = useRef(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);

  const persistUser = (user) => {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user }));
  };

  // Add automatic logout function
  const forceLogout = async () => {
    // Set flag to indicate this is a manual logout
    sessionStorage.setItem('manualLogout', 'true');
    
    // Clear sessionStorage
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    // Clear session preferences (table sorts, etc.)
    sessionStorage.removeItem('sessionPrefs');
    // Clear profile prefs cache (DB profile is kept for next login)
    await clearPreferences();
    sessionStorage.removeItem('mantis-react-ts-config');
    // Update state
    dispatch({ type: LOGOUT });
    // Redirect to login
    window.location.pathname = '/auth/login';
  };

  // Listen for force logout events from axios interceptor
  useEffect(() => {
    const handleForceLogout = () => {
      // Clear all authentication state
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      sessionStorage.removeItem('sessionPrefs');
      sessionStorage.removeItem('mantis-react-ts-config');
      
      // Dispatch logout to clear context state
      dispatch({ type: LOGOUT });
    };

    window.addEventListener('forceLogout', handleForceLogout);
    return () => window.removeEventListener('forceLogout', handleForceLogout);
  }, []);

  // Listen for reload permissions events from axios interceptor
  useEffect(() => {
    const handleReloadPermissions = (event) => {
      const { permissions } = event.detail;
      // Dispatch action to update permissions in context
      dispatch({ 
        type: RELOAD_PERMISSIONS, 
        payload: { permissions } 
      });
    };

    window.addEventListener('reloadPermissions', handleReloadPermissions);
    return () => window.removeEventListener('reloadPermissions', handleReloadPermissions);
  }, []);

  // Load user preferences whenever an authenticated session is restored
  useEffect(() => {
    let cancelled = false;

    const loadPreferencesForSession = async () => {
      if (!state.isLoggedIn) {
        preferencesInitRef.current = false;
        setPreferencesLoading(false);
        return;
      }

      if (preferencesInitRef.current) {
        return;
      }

      preferencesInitRef.current = true;
      setPreferencesLoading(true);

      try {
        await initializePreferences();
      } finally {
        if (!cancelled) {
          setPreferencesLoading(false);
        }
      }
    };

    loadPreferencesForSession();

    return () => {
      cancelled = true;
    };
  }, [state.isLoggedIn]);

  // Load stored auth data on mount
  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        const storedAuth = sessionStorage.getItem(AUTH_STORAGE_KEY);
        if (storedAuth) {
          const authData = JSON.parse(storedAuth);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: authData.user
            }
          });
        } else {
          // Mark as initialized if no stored data
          dispatch({ type: INITIALIZED });
        }
      } catch (error) {
        sessionStorage.removeItem(AUTH_STORAGE_KEY);
        // Mark as initialized after error
        dispatch({ type: INITIALIZED });
      }
    };

    loadStoredAuth();
  }, []);

  // Check authentication status from server only if no stored data
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Only check server if we don't have stored data and haven't already called checkAuth
      if (!sessionStorage.getItem(AUTH_STORAGE_KEY) && !checkAuthCalled.current) {
        checkAuthCalled.current = true;
        try {
          const response = await axios.get('/server/api/check_auth');
          
          if (response?.data?.success && response?.data?.data?.user) {
            const user = response.data.data.user;
            // Store in sessionStorage
            persistUser(user);
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user
              }
            });
          } else {
            // Clear remember cookie to prevent login loop
            document.cookie = 'remember_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            dispatch({ type: LOGOUT });
          }
        } catch (error) {
          // Handle errors (404, network errors, etc.)
          console.error('checkAuth error:', error);
          // Clear remember cookie to prevent login loop
          document.cookie = 'remember_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          dispatch({ type: LOGOUT });
        }
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (usernameOrEmail, password, dontRemember = false) => {
    try {
      // Clear any existing sessionStorage preferences on new login
      sessionStorage.removeItem('prefs');
      sessionStorage.removeItem('sessionPrefs');
      sessionStorage.removeItem('mantis-react-ts-config');
      
      // backend expects: { username, password, dontRemember }
      const body = { username: usernameOrEmail, password, dontRemember };
      const response = await axios.post('/server/api/login', body);
      
      if (!response?.data?.success || !response?.data?.data?.user) {
        throw new Error(response?.data?.message || 'Login failed');
      }
      
      const { user } = response.data.data;
      
      // Store in sessionStorage
      persistUser(user);
      
      // Set login time to prevent immediate redirects
      sessionStorage.setItem('lastLoginTime', Date.now().toString());

      // Update state (preferences load via isLoggedIn effect)
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user
        }
      });
      
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Set flag to indicate this is a manual logout
    sessionStorage.setItem('manualLogout', 'true');
    
    try {
      await axios.post('/server/api/logout');
    } catch (_) {
      // ignore
    }
    
    // Clear sessionStorage
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    // Clear session preferences (table sorts, etc.)
    sessionStorage.removeItem('sessionPrefs');
    // Clear profile prefs cache (DB profile is kept for next login)
    await clearPreferences();
    sessionStorage.removeItem('mantis-react-ts-config');
    
    dispatch({ type: LOGOUT });
    
    // Clear the manual logout flag after logout is complete
    setTimeout(() => {
      sessionStorage.removeItem('manualLogout');
    }, 1000);
  };

  const resetPassword = async () => {};
  const updateProfile = async (updatedFields) => {
    const nextUser = {
      ...(state.user || {}),
      ...(updatedFields || {})
    };

    persistUser(nextUser);
    dispatch({
      type: UPDATE_PROFILE,
      payload: {
        user: nextUser
      }
    });
  };

  // Force context value to always be a new object to ensure re-renders
  const contextValue = {
    ...state,
    login,
    logout,
    forceLogout,
    resetPassword,
    updateProfile
  };

  if (!state.isInitialized || (state.isLoggedIn && preferencesLoading)) {
    return <Loader />;
  }

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;
